import React, { useCallback } from 'react';
import { CONNECTION_CATEGORY_FILE } from 'js/constants/connectionCategories';
import SelectBucket from 'js/components/data_sources/add_ds_form/SelectBucket';
import useFileSystem from 'new/components/flows/configure/FileSystem/useFileSystem';
import flowTypes from 'new/components/flows/types/flowTypes';
import { useHistory } from 'react-router-dom';
import useFlowParams from '../../useFlowParams';

interface FileConfigureProps {
  connectionType: 'SINK' | 'SOURCE';
  formType?: string;
  sink?: any;
  source?: any;
  nexset?: any;
  nexsetSchema?: any;
  selectedBucket: any;
  allowCustomPrefixEditor?: boolean;
  shouldShowLoading?: boolean;
  hideMetaConfiguration?: boolean;
  selectedConnection: string;
  credentialId: number;
  sinkConfig?: any;
  sourceConfig?: any;
  renderSectionFields?: (section: string, fields: any[]) => any;
  onSourceConfigChanged?: (newConfig: any, field?: string, value?: any) => void;
  onSinkConfigChanged?: (newConfig: any, field?: string, value?: any) => void;
  onBucketSelected: (bucketConfig: any, selectedBucket: any) => void;
  flowType: keyof typeof flowTypes;
  onSaveAdvancedMode(data: any): void;
  nextFormStage(...args: any): any;
  onError?(msg: string): void;
}

export const FileSystem = (props: FileConfigureProps) => {
  const {
    connectionType,
    nexset,
    sink,
    source,
    credentialId,
    allowCustomPrefixEditor,
    selectedConnection,
    sinkConfig,
    sourceConfig,
    selectedBucket,
    nexsetSchema,
    onSourceConfigChanged,
    onSinkConfigChanged,
    onBucketSelected,
    renderSectionFields,
    shouldShowLoading,
    flowType,
    hideMetaConfiguration,
  } = props;
  const {
    paging,
    getProbeData,
    sinkConfigFieldsForFile,
    sourceConfigFields,
    onRequestProbeTree,
    sinkConfigFields,
    onToggleNodeProbeTree,
    setContextualSupport,
    unsetContextualSupport,
    selectedConnectionDetails,
  } = useFileSystem({
    selectedConnection,
    credentialId,
    flowType,
  });

  const probe = getProbeData(credentialId);
  const history = useHistory();
  const { isModal, onDismiss } = useFlowParams();
  const onInputChange = ({ property, value }) => {
    const newConfig = { ...sourceConfig, [property]: value };

    onSourceConfigChanged(newConfig);
  };

  const onSinkInputChange = ({ property, value }) => {
    // DataSourceDetails uses name="sourceName" / name="sourceDescription" for legacy reasons.
    // Normalize to name/description so save hooks find the user's edit in sinkConfig.name.
    const normalizedProperty =
      property === 'sourceName'
        ? 'name'
        : property === 'sourceDescription'
          ? 'description'
          : property;
    const newConfig = { ...sinkConfig, [normalizedProperty]: value };
    onSinkConfigChanged(newConfig);
  };

  const goBack = useCallback(() => {
    if (isModal) {
      onDismiss?.({});
      return;
    }
    history.goBack();
  }, [isModal, onDismiss, history]);

  return (
    // @ts-ignore
    <SelectBucket
      id={2}
      visibleFieldData={connectionType === 'SINK' ? sinkConfig : sourceConfig}
      shouldShowLoading={shouldShowLoading}
      hideMetaConfiguration={hideMetaConfiguration}
      sourceBeingEdited={source}
      selectedCredentialId={credentialId}
      selectedConnection={selectedConnection}
      connectionMetas={paging.metaInfoById}
      onInputChange={connectionType === 'SINK' ? onSinkInputChange : onInputChange}
      sourceName={
        connectionType === 'SINK'
          ? sink?.name || sinkConfig?.name
          : source?.name || sourceConfig?.name
      }
      sourceDescription={
        connectionType === 'SINK'
          ? sink?.description || sinkConfig?.description
          : source?.description || sourceConfig?.description
      }
      selectedConnectionDetails={selectedConnectionDetails}
      dataSet={nexset}
      data={sink}
      isReplication={flowType === flowTypes.replication}
      allowCustomPrefixEditor={allowCustomPrefixEditor}
      sourceConfigFields={connectionType === 'SINK' ? sinkConfigFieldsForFile : sourceConfigFields}
      sourceConfig={connectionType === 'SINK' ? sinkConfig : sourceConfig}
      enableFileSelection={connectionType === 'SINK' ? true : selectedConnection === 'gdrive'}
      selectableFileMimeType="application/vnd.google-apps.spreadsheet"
      onSourceConfigChanged={
        connectionType === 'SINK' ? onSinkConfigChanged : onSourceConfigChanged
      }
      onValidationComplete={() => {}}
      onSelected={onBucketSelected}
      onRequestProbeTree={onRequestProbeTree}
      onToggleNodeProbeTree={onToggleNodeProbeTree}
      probeData={probe.data}
      probeError={probe.error}
      probeTreeLastUpdatedAt={probe.lastUpdated}
      isLoadingProbeData={probe.loading}
      onRequestSourceCreation={() => {}}
      selectedBucket={selectedBucket}
      dataSchema={nexsetSchema}
      resourceMode="sink"
      paging={paging}
      selectedDestinationType={selectedConnection}
      flowType={flowType}
      sinkConfigFields={sinkConfigFields}
      addedDataSink={sink}
      dataSetId={nexset?.id}
      sinkConfig={sinkConfig}
      renderSectionFields={renderSectionFields}
      setContextualSupport={setContextualSupport}
      unsetContextualSupport={unsetContextualSupport}
      connectionCategory={CONNECTION_CATEGORY_FILE}
      onSinkConfigChanged={onSinkConfigChanged}
      onSaveAdvancedMode={props?.onSaveAdvancedMode}
      nextFormStage={props?.nextFormStage}
      goBack={goBack}
      onError={props?.onError}
    />
  );
};

FileSystem.displayName = 'FileSystem';
